import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import { usePromptStore } from "../../hooks/usePromptStore";

const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  "이미지 생성": { bg: "#FFF3E0", text: "#FF8C00", border: "#FFE0B2" },
  "업무 자동화": { bg: "#E8F5E9", text: "#2E7D32", border: "#C8E6C9" },
  "콘텐츠 글쓰기": { bg: "#FCE4EC", text: "#C62828", border: "#FFCDD2" },
  "챗봇 개발": { bg: "#E3F2FD", text: "#1565C0", border: "#BBDEFB" },
  "카피라이팅": { bg: "#F3E5F5", text: "#6A1B9A", border: "#E1BEE7" },
  "프롬프트": { bg: "#F2F4F6", text: "#4A5568", border: "#E0E2E6" },
};

function CopyToast({ show }: { show: boolean }) {
  return (
    <div
      className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-[#191F28] text-white px-5 py-3 rounded-2xl text-sm font-bold transition-all duration-300 ${
        show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
    >
      📋 클립보드에 복사됐어요!
    </div>
  );
}

function EmptyState({ onExplore }: { onExplore: () => void }) {
  return (
    <div className="py-20 flex flex-col items-center text-center">
      <div className="w-20 h-20 rounded-3xl bg-[#F2F4F6] flex items-center justify-center mb-5">
        <i className="ri-book-open-line text-4xl text-[#C8CDD4]" />
      </div>
      <h3 className="text-xl font-black text-[#191F28] mb-2">저장된 프롬프트가 없어요</h3>
      <p className="text-sm text-[#8B95A1] leading-relaxed mb-6 max-w-xs">
        클래스 실습 단계에서 프롬프트를 만들고<br />
        노트북에 저장해보세요!
      </p>
      <button
        onClick={onExplore}
        className="bg-[#00D873] text-white font-bold px-6 py-3 rounded-2xl cursor-pointer hover:opacity-90 transition-all whitespace-nowrap"
      >
        클래스 탐색하기 →
      </button>
    </div>
  );
}

function PromptCard({
  prompt,
  onDelete,
  onCopy,
  onReuse,
}: {
  prompt: ReturnType<typeof usePromptStore>["prompts"][0];
  onDelete: (id: string) => void;
  onCopy: (text: string) => void;
  onReuse: (classId: number) => void;
}) {
  const colors = CATEGORY_COLORS[prompt.category] ?? CATEGORY_COLORS["프롬프트"];
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div
      className="bg-white rounded-2xl p-5 border-2 transition-all hover:-translate-y-0.5"
      style={{ borderColor: colors.border }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span
          className="text-xs font-bold px-2.5 py-1 rounded-full"
          style={{ backgroundColor: colors.bg, color: colors.text }}
        >
          {prompt.category}
        </span>
        <span className="text-xs text-[#8B95A1]">{prompt.savedAt}</span>
      </div>

      {/* Class badge */}
      <div className="flex items-center gap-1.5 mb-3">
        <i className="ri-graduation-cap-line text-xs text-[#8B95A1]" />
        <span className="text-xs text-[#8B95A1]">{prompt.classTitle}</span>
      </div>

      {/* Prompt text */}
      <div className="bg-[#0D1117] rounded-xl p-4 mb-4 min-h-[72px]">
        <code className="text-[#00D873] text-xs font-mono leading-relaxed break-words line-clamp-3">
          {prompt.prompt}
        </code>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => onCopy(prompt.prompt)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-[#F2F4F6] hover:bg-[#E8E9EB] transition-colors text-xs font-bold text-[#191F28] cursor-pointer whitespace-nowrap"
        >
          <i className="ri-file-copy-line" />
          복사
        </button>
        <button
          onClick={() => onReuse(prompt.classId)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-white text-xs font-bold cursor-pointer hover:opacity-90 transition-all whitespace-nowrap"
          style={{ backgroundColor: colors.text }}
        >
          <i className="ri-refresh-line" />
          다시 실습
        </button>
        {confirmDelete ? (
          <button
            onClick={() => onDelete(prompt.id)}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#FF6B6B] text-white cursor-pointer whitespace-nowrap shrink-0"
          >
            <i className="ri-delete-bin-line text-sm" />
          </button>
        ) : (
          <button
            onClick={() => setConfirmDelete(true)}
            onBlur={() => setTimeout(() => setConfirmDelete(false), 200)}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F2F4F6] text-[#C8CDD4] hover:text-[#FF6B6B] transition-colors cursor-pointer whitespace-nowrap shrink-0"
          >
            <i className="ri-delete-bin-line text-sm" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function NotebookPage() {
  const { prompts, deletePrompt } = usePromptStore();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("전체");
  const [showCopyToast, setShowCopyToast] = useState(false);

  const categories = ["전체", ...Array.from(new Set(prompts.map((p) => p.category)))];

  const filtered = useMemo(() => {
    return prompts.filter((p) => {
      const matchCat = activeCategory === "전체" || p.category === activeCategory;
      const matchSearch =
        !search ||
        p.prompt.toLowerCase().includes(search.toLowerCase()) ||
        p.classTitle.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [prompts, activeCategory, search]);

  const handleCopy = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => {});
    setShowCopyToast(true);
    setTimeout(() => setShowCopyToast(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#F2F4F6] font-sans">
      <Navbar />
      <CopyToast show={showCopyToast} />

      <div className="max-w-4xl mx-auto px-4 pt-24 pb-16">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 mb-8">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">📓</span>
              <h1 className="text-3xl font-black text-[#191F28]">프롬프트 노트북</h1>
            </div>
            <p className="text-[#8B95A1] text-sm">
              실습에서 만든 프롬프트를 저장하고, 언제든 꺼내 쓸 수 있어요.
            </p>
          </div>
          {prompts.length > 0 && (
            <div className="bg-white rounded-2xl px-5 py-3 text-center shrink-0">
              <div className="text-2xl font-black text-[#191F28]">{prompts.length}</div>
              <div className="text-xs text-[#8B95A1]">저장된 프롬프트</div>
            </div>
          )}
        </div>

        {prompts.length === 0 ? (
          <div className="bg-white rounded-3xl">
            <EmptyState onExplore={() => navigate("/explore")} />
          </div>
        ) : (
          <>
            {/* Search + Filter */}
            <div className="bg-white rounded-2xl p-4 mb-6 flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#8B95A1]" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="프롬프트 내용 또는 클래스명 검색..."
                  className="w-full pl-9 pr-4 py-2.5 bg-[#F2F4F6] rounded-xl text-sm text-[#191F28] placeholder-[#8B95A1] outline-none focus:ring-2 focus:ring-[#00D873]/30"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex gap-2 flex-wrap mb-6">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                    activeCategory === cat
                      ? "bg-[#191F28] text-white"
                      : "bg-white text-[#8B95A1] hover:text-[#191F28]"
                  }`}
                >
                  {cat}
                  {cat !== "전체" && (
                    <span className="ml-1 opacity-60">
                      {prompts.filter((p) => p.category === cat).length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Results count */}
            {search && (
              <p className="text-sm text-[#8B95A1] mb-4">
                &quot;{search}&quot; 검색 결과 {filtered.length}개
              </p>
            )}

            {/* Grid */}
            {filtered.length === 0 ? (
              <div className="bg-white rounded-2xl py-16 text-center">
                <i className="ri-search-line text-4xl text-[#C8CDD4] block mb-3" />
                <p className="text-sm text-[#8B95A1]">검색 결과가 없어요</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {filtered.map((p) => (
                  <PromptCard
                    key={p.id}
                    prompt={p}
                    onDelete={deletePrompt}
                    onCopy={handleCopy}
                    onReuse={(classId) => navigate(`/learn/${classId}`)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
